import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';

/** Response from GET /stats/player-full-stats/:leagueId/:rosterPlayerId (shape may vary slightly by backend). */
export type PlayerFullStatsPayload = Record<string, unknown>;

function firstArray(payload: PlayerFullStatsPayload | undefined, keys: string[]): unknown[] {
  if (!payload) return [];
  for (const k of keys) {
    const v = payload[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

export function getBattingStatRows(payload: PlayerFullStatsPayload | undefined): Record<string, unknown>[] {
  return firstArray(payload, [
    'simBattingStats',
    'sim_batting_stats',
    'simStatsBatting',
    'battingStats',
    'sim_stats_batting',
    'batting',
  ]) as Record<string, unknown>[];
}

export function getPitchingStatRows(payload: PlayerFullStatsPayload | undefined): Record<string, unknown>[] {
  return firstArray(payload, [
    'simPitchingStats',
    'sim_pitching_stats',
    'simStatsPitching',
    'pitchingStats',
    'sim_stats_pitching',
    'pitching',
  ]) as Record<string, unknown>[];
}

export function rowSeasonLabel(row: Record<string, unknown>): string {
  const s = row.season ?? row.year ?? row.sim_year ?? row.simYear;
  if (s == null || s === '') return '—';
  return String(s);
}

/** Lower number sorts first (YTD before last-7-days, etc.). */
export function periodSortKey(period: unknown): number {
  const s = String(period ?? '')
    .toLowerCase()
    .trim();
  if (s === 'ytd') return 0;
  if (s === '7_days' || s === '7 days' || s === 'last_7_days') return 1;
  return 50;
}

/** Short label for sim_stats period (batting/pitching rows). */
export function periodShortLabel(period: unknown): string {
  const s = String(period ?? '')
    .toLowerCase()
    .trim();
  if (s === 'ytd') return 'YTD';
  if (s === '7_days' || s === '7 days' || s === 'last_7_days') return 'L7d';
  if (!s) return '—';
  return s.toUpperCase();
}

/** Sort by season (newest first), then period (YTD before 7-day, etc.). */
export function sortSimStatRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...rows].sort((a, b) => {
    const na = Number(a.season ?? a.year);
    const nb = Number(b.season ?? b.year);
    const aOk = Number.isFinite(na);
    const bOk = Number.isFinite(nb);
    if (aOk && bOk && na !== nb) return nb - na;
    if (aOk && !bOk) return -1;
    if (!aOk && bOk) return 1;
    const pd = periodSortKey(a.period) - periodSortKey(b.period);
    if (pd !== 0) return pd;
    return String(a.id ?? '').localeCompare(String(b.id ?? ''));
  });
}

export interface UsePlayerFullStatsOptions {
  leagueId: number | undefined;
  rosterPlayerId: number | undefined;
  /** Passed as `season` query param when set (limits sim stat rows to that season). */
  season?: number;
  enabled?: boolean;
}

export function usePlayerFullStats({
  leagueId,
  rosterPlayerId,
  season,
  enabled = true,
}: UsePlayerFullStatsOptions) {
  return useQuery({
    queryKey: ['playerFullStats', leagueId, rosterPlayerId, season],
    queryFn: async () => {
      if (leagueId == null || rosterPlayerId == null) {
        throw new Error('leagueId and rosterPlayerId are required');
      }
      const res = await apiClient.get<PlayerFullStatsPayload>(
        `/stats/player-full-stats/${leagueId}/${rosterPlayerId}`,
        {
          params:
            season != null && Number.isFinite(season) ? { season } : undefined,
        }
      );
      return res.data;
    },
    enabled: enabled && leagueId != null && rosterPlayerId != null,
    staleTime: 60_000,
  });
}
